import {Form} from 'antd';
import {DefaultOptionType} from 'antd/lib/select';
import dayjs, {Dayjs} from 'dayjs';
import {isEqual, isNil} from 'lodash';
import {NoUndefinedRangeValueType} from 'rc-picker/lib/PickerInput/RangePicker';
import {KeyboardEvent, useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {useLicenses} from '../../../../../provider/license/license-hook';
import {useLocale} from '../../../../../provider/locale/locale-hook';
import {MainFilterContext} from '../../../../../provider/main-filter/main-filter';
import {MainFilterContextType} from '../../../../../provider/main-filter/main-filter-type';
import {usePersonality} from '../../../../../provider/personality/use-personality';
import {useReviewsDoctorList} from '../../../../../service/reviews/reviews-doctors';
import {useReviewsReplierList} from '../../../../../service/reviews/reviews-repliers';
import {useResponsibleUsers} from '../../../../../service/reviews/reviews-responsible-users';
import {useReviewsStatistic} from '../../../../../service/reviews/reviews-statistic';
import {ReviewStatusType, useReviewStatusList} from '../../../../../service/reviews/reviews-statuses';
import {FeaturesEnum} from '../../../../../service/user/user-type';
import {debounce} from '../../../../../util/function';
import {useUrl} from '../../../../../util/url-hook/url-hook';
import {
    ReviewsSavedFilterPeriodPickerTabType,
    ReviewsSavedFilterQueryType,
    ReviewsSavedFilterUiStateType,
} from '../../reviews-state/reviews-saved-filter-helper';
import {defaultReviewsState} from '../../reviews-state/reviews-state-const';
import {reviewsFilterStateToUrlObject} from '../../reviews-state/reviews-state-helper';
import {useReviewStateHook} from '../../reviews-state/reviews-state-provider';
import {ReviewsStateType, ReviewsStateUrlQueryNameEnum} from '../../reviews-state/reviews-state-type';
import {
    getRatingListCountForKey,
    getWeightedAverageRatingForSelectedRatings,
} from '../reviews-statistic/reviews-statistic-helper';

import {getReviewsFilterDataFromForm, trackAnalytics} from './reviews-filter-helper';
import {
    AbuseStatusEnum,
    AutoAbuseEnum,
    AutoReplayEnum,
    ContentOfReviewsEnum,
    DeletedReviewsEnum,
    DoctorsReviewsEnum,
    EmployeesReviewsEnum,
    HasNotesEnum,
    ModeratedEnum,
    PossibilityOfAnAnswerEnum,
    RepliesToReviewsEnum,
    ReviewChangedEnum,
    ReviewNameEnum,
    ReviewsFilterDataType,
    ReviewsFilterFieldNameEnum,
    WaitingForSecondAnswerEnum,
    WithoutTagEnum,
} from './reviews-filter-type';

const RATING_FILTER_ORDER = [5, 4, 3, 2, 1, 0];
const SAVED_FILTER_SAVE_DEBOUNCE = 500;
const ANALYTICS_ALL_SELECTED_LABEL = 'all selected';

type CustomOptionType = DefaultOptionType & {
    statusInfo: ReviewStatusType;
};

export enum ReviewsFilterSectionEnum {
    main = 'main',
    employee = 'employee',
    tags = 'tags',
    additional = 'additional',
}

const ALL_SECTION_KEYS = Object.values(ReviewsFilterSectionEnum);

function getExpandedSectionsValue(isSavedFilterEnabled: boolean, savedExpandedSections?: Array<string>): Array<string> {
    if (!isSavedFilterEnabled) {
        return ALL_SECTION_KEYS;
    }

    return savedExpandedSections ?? ALL_SECTION_KEYS;
}

type ReviewsFilterMainSectionActiveInputType = {
    reviewFilterState: ReviewsFilterDataType;
    dateFrom: Dayjs | null;
    dateTo: Dayjs | null;
    collectionDateFrom: Dayjs | null;
    collectionDateTo: Dayjs | null;
    hasDoctors: boolean | null | undefined;
};

function isReviewsFilterMainSectionDateOrSourceActive(
    reviewFilterState: ReviewsFilterDataType,
    dateFrom: Dayjs | null,
    dateTo: Dayjs | null,
    collectionDateFrom: Dayjs | null,
    collectionDateTo: Dayjs | null
): boolean {
    return (
        reviewFilterState[ReviewsFilterFieldNameEnum.sourceList].length > 0 ||
        (Boolean(dateFrom) && Boolean(dateTo)) ||
        (Boolean(collectionDateFrom) && Boolean(collectionDateTo))
    );
}

function isReviewsFilterMainSectionRestActive(
    reviewFilterState: ReviewsFilterDataType,
    hasDoctors: boolean | null | undefined
): boolean {
    return (
        (Boolean(hasDoctors) && reviewFilterState[ReviewsFilterFieldNameEnum.doctorNames].length > 0) ||
        reviewFilterState[ReviewsFilterFieldNameEnum.processStatus].length > 0 ||
        reviewFilterState[ReviewsFilterFieldNameEnum.customProcessStatus].length > 0 ||
        reviewFilterState[ReviewsFilterFieldNameEnum.repliesToReviews] !== RepliesToReviewsEnum.noSelected ||
        reviewFilterState[ReviewsFilterFieldNameEnum.contentOfReviews] !== ContentOfReviewsEnum.noSelected ||
        reviewFilterState[ReviewsFilterFieldNameEnum.possibilityOfAnAnswer] !== PossibilityOfAnAnswerEnum.noSelected
    );
}

function isReviewsFilterMainSectionActive(input: ReviewsFilterMainSectionActiveInputType): boolean {
    const {reviewFilterState, dateFrom, dateTo, collectionDateFrom, collectionDateTo, hasDoctors} = input;

    return (
        isReviewsFilterMainSectionDateOrSourceActive(
            reviewFilterState,
            dateFrom,
            dateTo,
            collectionDateFrom,
            collectionDateTo
        ) || isReviewsFilterMainSectionRestActive(reviewFilterState, hasDoctors)
    );
}

function isReviewsFilterEmployeeSectionActive(
    reviewFilterState: ReviewsFilterDataType,
    replyDateFrom: Dayjs | null,
    replyDateTo: Dayjs | null
): boolean {
    return (
        reviewFilterState[ReviewsFilterFieldNameEnum.employeesResponsible].length > 0 ||
        reviewFilterState[ReviewsFilterFieldNameEnum.employeesResponding].length > 0 ||
        reviewFilterState[ReviewsFilterFieldNameEnum.agencyRepliers].length > 0 ||
        (Boolean(replyDateFrom) && Boolean(replyDateTo))
    );
}

function isReviewsFilterTagsSectionActive(reviewFilterState: ReviewsFilterDataType): boolean {
    return (
        reviewFilterState[ReviewsFilterFieldNameEnum.withoutTag] !== WithoutTagEnum.noSelected ||
        reviewFilterState[ReviewsFilterFieldNameEnum.tagList].length > 0
    );
}

function isReviewsFilterAdditionalSectionActive(reviewFilterState: ReviewsFilterDataType): boolean {
    return (
        reviewFilterState[ReviewsFilterFieldNameEnum.hasNotes] !== HasNotesEnum.noSelected ||
        reviewFilterState[ReviewsFilterFieldNameEnum.deletedReviews] !== DeletedReviewsEnum.noSelected ||
        reviewFilterState[ReviewsFilterFieldNameEnum.abuseStatus].length > 0 ||
        reviewFilterState[ReviewsFilterFieldNameEnum.autoReplay].length > 0 ||
        reviewFilterState[ReviewsFilterFieldNameEnum.autoAbuse] !== AutoAbuseEnum.notSelected ||
        reviewFilterState[ReviewsFilterFieldNameEnum.reviewChanged] !== ReviewChangedEnum.noSelected ||
        reviewFilterState[ReviewsFilterFieldNameEnum.waitingForSecondAnswer] !==
            WaitingForSecondAnswerEnum.noSelected ||
        reviewFilterState[ReviewsFilterFieldNameEnum.reviewType] !== ReviewNameEnum.noSelected ||
        reviewFilterState[ReviewsFilterFieldNameEnum.reviewModerated] !== ModeratedEnum.noSelected
    );
}

// eslint-disable-next-line max-statements, @typescript-eslint/explicit-module-boundary-types, sonarjs/cognitive-complexity
export function useReviewsFilter() {
    const {
        reviewsState,
        isShowAutotags,
        reviewsCatalogListIsInProgress,
        reviewsCatalogList,
        setReviewsStateUrlQuery,
        isSavedFilterEnabled,
        isSavedFilterLoading,
        isSavedFilterSaving,
        savedFilterUiState,
        enableSavedFilter,
        disableSavedFilter,
        saveCurrentFilterState,
        resetSavedFilter,
    } = useReviewStateHook();

    const {getLocalizedString, localeName} = useLocale();
    const [form] = Form.useForm();
    const {licenses} = useLicenses();
    const hasMedicineCatalog = reviewsCatalogList?.some((catalog) => catalog.is_medicine);
    const internalAgencyReviewLicense = licenses[FeaturesEnum.bulkGenerateButton]?.isActive;
    const {data: reviewsReplierList, isFetching: reviewsReplierListIsInProgress} = useReviewsReplierList();
    const {data: reviewsDoctorList, isFetching: reviewsDoctorListIsInProgress} = useReviewsDoctorList(
        Boolean(hasMedicineCatalog)
    );
    const {data: reviewStatusList, isFetching: isReviewStatusListInProgress} = useReviewStatusList(localeName);
    const {queries} = useUrl<Omit<ReviewsStateType, 'filterState'> & ReviewsFilterDataType>();
    const {filterState: reviewFilterState} = reviewsState;
    const reviewsStateWithAnyRating = useMemo(
        () => ({
            ...reviewsState,
            filterState: {
                ...reviewFilterState,
                [ReviewsFilterFieldNameEnum.ratingList]: [],
            },
        }),
        [reviewFilterState, reviewsState]
    );
    const {data: reviewsStatisticResult, isFetching: isReviewsStatisticFetching} =
        useReviewsStatistic(reviewsStateWithAnyRating);

    const {mainFilterKey} = useContext<MainFilterContextType>(MainFilterContext);

    const filterKey = useMemo(() => ({filterKey: mainFilterKey}), [mainFilterKey]);
    const {data: responsibleUsersList, isFetching: isReviewResponsibleUsersInProgress} = useResponsibleUsers(filterKey);

    const dateFromRaw = reviewFilterState[ReviewsFilterFieldNameEnum.dateFrom];
    const dateFrom = dateFromRaw ? dayjs(dateFromRaw) : null;
    const dateToRaw = reviewFilterState[ReviewsFilterFieldNameEnum.dateTo];
    const dateTo = dateToRaw ? dayjs(dateToRaw) : null;
    const collectionDateFromRaw = reviewFilterState[ReviewsFilterFieldNameEnum.collectionDateFrom];
    const collectionDateFrom = collectionDateFromRaw ? dayjs(collectionDateFromRaw) : null;
    const collectionDateToRaw = reviewFilterState[ReviewsFilterFieldNameEnum.collectionDateTo];
    const collectionDateTo = collectionDateToRaw ? dayjs(collectionDateToRaw) : null;
    const replyDateFromRaw = reviewFilterState[ReviewsFilterFieldNameEnum.replyDateFrom];
    const replyDateFrom = replyDateFromRaw ? dayjs(replyDateFromRaw) : null;
    const replyDateToRaw = reviewFilterState[ReviewsFilterFieldNameEnum.replyDateTo];
    const replyDateTo = replyDateToRaw ? dayjs(replyDateToRaw) : null;
    const today = dayjs().format('YYYY-MM-DD');
    const thirtyDaysAgo = dayjs().subtract(30, 'day').format('YYYY-MM-DD');
    const {isBigBrand} = usePersonality();

    const defaultReviewsStateMagnitIncluding = useMemo(
        () =>
            isBigBrand
                ? {
                      ...defaultReviewsState.filterState,
                      [ReviewsFilterFieldNameEnum.dateFrom]: new Date(thirtyDaysAgo),
                      [ReviewsFilterFieldNameEnum.dateTo]: new Date(today),
                  }
                : defaultReviewsState.filterState,
        [isBigBrand, thirtyDaysAgo, today]
    );

    const isFilterEmpty = useMemo(
        () => isEqual(reviewFilterState, defaultReviewsStateMagnitIncluding),
        [defaultReviewsStateMagnitIncluding, reviewFilterState]
    );

    const [periodPickerActiveTab, setPeriodPickerActiveTab] = useState<ReviewsSavedFilterPeriodPickerTabType>(
        savedFilterUiState.periodPickerActiveTab
    );

    const [expandedSections, setExpandedSections] = useState<Array<string>>(
        getExpandedSectionsValue(isSavedFilterEnabled, savedFilterUiState.expandedSections)
    );

    const getCurrentSavedFilterUiState = useCallback(
        (
            nextPeriodPickerActiveTab = periodPickerActiveTab,
            nextExpandedSections = expandedSections
        ): ReviewsSavedFilterUiStateType => ({
            ...savedFilterUiState,
            periodPickerActiveTab: nextPeriodPickerActiveTab,
            expandedSections: nextExpandedSections,
        }),
        [expandedSections, periodPickerActiveTab, savedFilterUiState]
    );

    function handleSectionsChange(keys: string | Array<string>) {
        const next = Array.isArray(keys) ? keys : [keys];

        setExpandedSections(next);
        saveCurrentFilterState(
            reviewsFilterStateToUrlObject(getReviewsFilterDataFromForm(form)),
            getCurrentSavedFilterUiState(periodPickerActiveTab, next)
        );
    }

    const saveCurrentFilterStateDebounced = useMemo(
        () =>
            debounce((query: ReviewsSavedFilterQueryType, uiState: ReviewsSavedFilterUiStateType) => {
                saveCurrentFilterState(query, uiState);
            }, SAVED_FILTER_SAVE_DEBOUNCE),
        [saveCurrentFilterState]
    );

    function onFormReset() {
        const defaultFilterQuery = reviewsFilterStateToUrlObject(defaultReviewsStateMagnitIncluding);

        setReviewsStateUrlQuery({
            ...defaultFilterQuery,
            [ReviewsStateUrlQueryNameEnum.ids]: '',
        });
        resetSavedFilter(defaultFilterQuery, getCurrentSavedFilterUiState());
    }

    const onFieldsChange = useCallback(() => {
        const nextFilterQuery = reviewsFilterStateToUrlObject(getReviewsFilterDataFromForm(form));

        setReviewsStateUrlQuery(nextFilterQuery);
        saveCurrentFilterStateDebounced(nextFilterQuery, getCurrentSavedFilterUiState());
    }, [form, getCurrentSavedFilterUiState, saveCurrentFilterStateDebounced, setReviewsStateUrlQuery]);

    function handleSavedFilterToggle() {
        if (isSavedFilterLoading || isSavedFilterSaving) {
            return;
        }

        if (isSavedFilterEnabled) {
            disableSavedFilter();

            return;
        }

        enableSavedFilter(
            reviewsFilterStateToUrlObject(getReviewsFilterDataFromForm(form)),
            getCurrentSavedFilterUiState()
        );
    }

    function handleSavedFilterKeyDown(event: KeyboardEvent<HTMLDivElement>) {
        if (event.key !== 'Enter' && event.key !== ' ') {
            return;
        }

        event.preventDefault();
        handleSavedFilterToggle();
    }

    function handleDoctorsChange(value: Array<string>) {
        const lastSelected = value[value.length - 1];

        if (lastSelected === DoctorsReviewsEnum.all) {
            form.setFieldsValue({[ReviewsFilterFieldNameEnum.doctorNames]: [DoctorsReviewsEnum.all]});
        } else {
            form.setFieldsValue({
                [ReviewsFilterFieldNameEnum.doctorNames]: value.filter((value_) => value_ !== DoctorsReviewsEnum.all),
            });
        }

        onFieldsChange();

        trackAnalytics(ReviewsFilterFieldNameEnum.doctorNames, value);
    }

    function handleSourceListChange(value: Array<number>) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.sourceList]: value});
        onFieldsChange();

        const sources = reviewsCatalogList
            ? reviewsCatalogList
                  .filter((catalog) => value.includes(catalog.id))
                  .map((catalog) => ({id: catalog.id, name: catalog.name}))
            : [];

        trackAnalytics(
            ReviewsFilterFieldNameEnum.sourceList,
            sources.map((source) => source.name)
        );
    }

    function handleRatingChange(value: Array<number>) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.ratingList]: value});
        onFieldsChange();

        trackAnalytics(ReviewsFilterFieldNameEnum.ratingList, value);
    }

    function handleResponsibleEmployeeChange(value: Array<number | string>) {
        const lastSelected = value[value.length - 1];

        if (lastSelected === EmployeesReviewsEnum.all) {
            form.setFieldsValue({[ReviewsFilterFieldNameEnum.employeesResponsible]: [EmployeesReviewsEnum.all]});
        } else {
            form.setFieldsValue({
                [ReviewsFilterFieldNameEnum.employeesResponsible]: value.filter(
                    (value_) => value_ !== EmployeesReviewsEnum.all
                ),
            });
        }

        onFieldsChange();

        trackAnalytics(ReviewsFilterFieldNameEnum.employeesResponsible, value);
    }

    function handleRespondingEmployeeChange(value: Array<number | string>) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.employeesResponding]: value});
        onFieldsChange();

        trackAnalytics(ReviewsFilterFieldNameEnum.employeesResponding, value);
    }

    function handleStatusListChange(_value: Array<string>, options: DefaultOptionType | Array<DefaultOptionType>) {
        const customProcessStatus = (options as Array<CustomOptionType>)
            .filter((option) => !option.statusInfo.isDefault)
            .map((status) => status.statusInfo.statusCode);
        const defaultProcessStatus = (options as Array<CustomOptionType>)
            .filter((option) => option.statusInfo.isDefault)
            .map((status) => status.statusInfo.statusCode);

        form.setFieldsValue({
            [ReviewsFilterFieldNameEnum.processStatus]: defaultProcessStatus,
            [ReviewsFilterFieldNameEnum.customProcessStatus]: customProcessStatus,
        });
        onFieldsChange();
        trackAnalytics(ReviewsFilterFieldNameEnum.processStatus, defaultProcessStatus);
        trackAnalytics(ReviewsFilterFieldNameEnum.customProcessStatus, customProcessStatus);
    }

    const handleNewTagsChange = useCallback(
        (value: Array<number | string>) => {
            form.setFieldsValue({[ReviewsFilterFieldNameEnum.tagList]: value});
            onFieldsChange();
            trackAnalytics(ReviewsFilterFieldNameEnum.tagList, value);
        },
        [form, onFieldsChange]
    );

    const handleAgencyRepliersChange = useCallback(
        (value: Array<number | string>) => {
            form.setFieldsValue({[ReviewsFilterFieldNameEnum.agencyRepliers]: value});
            onFieldsChange();
        },
        [form, onFieldsChange]
    );

    function handleWithoutTagChange(value: WithoutTagEnum) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.withoutTag]: value});

        if (value === WithoutTagEnum.withoutTagTrue) {
            form.setFieldsValue({[ReviewsFilterFieldNameEnum.tagList]: []});
        }

        onFieldsChange();

        const eventPayload = {
            [WithoutTagEnum.noSelected]: 'show all',
            [WithoutTagEnum.withoutTagTrue]: 'without tags',
            [WithoutTagEnum.withoutTagFalse]: 'with tags',
            null: 'all tags',
        }[value as WithoutTagEnum];

        trackAnalytics(ReviewsFilterFieldNameEnum.withoutTag, eventPayload);
    }

    function handleAutoReplyChange(value: Array<AutoReplayEnum>) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.autoReplay]: value});

        trackAnalytics(ReviewsFilterFieldNameEnum.autoReplay, value);
    }

    function handleContentOfReviewsChange(value: ContentOfReviewsEnum) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.contentOfReviews]: value});
        onFieldsChange();

        const type = {
            [ContentOfReviewsEnum.noSelected]: ANALYTICS_ALL_SELECTED_LABEL,
            [ContentOfReviewsEnum.reviewsWithText]: 'with text',
            [ContentOfReviewsEnum.reviewsWithoutText]: 'without text',
        }[value as ContentOfReviewsEnum];

        trackAnalytics(ReviewsFilterFieldNameEnum.contentOfReviews, type);
    }

    function handleHasNotesChange(value: HasNotesEnum) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.hasNotes]: value});
        onFieldsChange();

        const type = {
            [HasNotesEnum.noSelected]: ANALYTICS_ALL_SELECTED_LABEL,
            [HasNotesEnum.yes]: 'yes',
            [HasNotesEnum.no]: 'no',
        }[value];

        trackAnalytics(ReviewsFilterFieldNameEnum.hasNotes, type);
    }

    function handleDeletedReviewsChange(value: DeletedReviewsEnum) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.deletedReviews]: value});
        onFieldsChange();

        const type = {
            [DeletedReviewsEnum.noSelected]: ANALYTICS_ALL_SELECTED_LABEL,
            [DeletedReviewsEnum.yes]: 'yes',
            [DeletedReviewsEnum.no]: 'no',
        }[value as DeletedReviewsEnum];

        trackAnalytics(ReviewsFilterFieldNameEnum.deletedReviews, type);
    }

    function handleRepliesToReviewsChange(value: RepliesToReviewsEnum) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.repliesToReviews]: value});
        onFieldsChange();

        const type = {
            [RepliesToReviewsEnum.noSelected]: ANALYTICS_ALL_SELECTED_LABEL,
            [RepliesToReviewsEnum.ReviewsUnanswered]: 'without response',
            [RepliesToReviewsEnum.ReviewsWithAResponse]: 'with response',
        }[value as RepliesToReviewsEnum];

        trackAnalytics(ReviewsFilterFieldNameEnum.repliesToReviews, type);
    }

    function handlePossibilityOfAnswerChange(value: PossibilityOfAnAnswerEnum) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.possibilityOfAnAnswer]: value});
        onFieldsChange();

        const type = {
            [PossibilityOfAnAnswerEnum.noSelected]: ANALYTICS_ALL_SELECTED_LABEL,
            [PossibilityOfAnAnswerEnum.HasNoPossibilityOfAnAnswer]: 'has no possibility to answer',
            [PossibilityOfAnAnswerEnum.HasPossibilityOfAnAnswer]: 'has possibility to answer',
        }[value as PossibilityOfAnAnswerEnum];

        trackAnalytics(ReviewsFilterFieldNameEnum.possibilityOfAnAnswer, type);
    }

    function handleReviewTypeChange(value: ReviewNameEnum) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.reviewType]: value});
        onFieldsChange();

        const type = {
            [ReviewNameEnum.noSelected]: ANALYTICS_ALL_SELECTED_LABEL,
            [ReviewNameEnum.ReviewsForCompanies]: 'for companies',
            [ReviewNameEnum.ReviewsForTheBrand]: 'for brand',
        }[value as ReviewNameEnum];

        trackAnalytics(ReviewsFilterFieldNameEnum.reviewType, type);
    }

    function handleAbuseStatusChange(value: Array<AbuseStatusEnum>) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.abuseStatus]: value});
        onFieldsChange();
        trackAnalytics(ReviewsFilterFieldNameEnum.abuseStatus, value);
    }

    function handleAutoAbuseChange(value?: AutoAbuseEnum | null) {
        const next: AutoAbuseEnum =
            value === AutoAbuseEnum.yes || value === AutoAbuseEnum.no ? value : AutoAbuseEnum.notSelected;

        form.setFieldsValue({[ReviewsFilterFieldNameEnum.autoAbuse]: next});

        trackAnalytics(ReviewsFilterFieldNameEnum.autoAbuse, next === AutoAbuseEnum.notSelected ? '' : String(next));
    }

    function handleReviewChangedChange(value: ReviewChangedEnum) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.reviewChanged]: value});
        onFieldsChange();

        const type = {
            [ReviewChangedEnum.yes]: 'yes',
            [ReviewChangedEnum.no]: 'no',
            [ReviewChangedEnum.noSelected]: 'no selected',
        }[value as ReviewChangedEnum];

        trackAnalytics(ReviewsFilterFieldNameEnum.reviewChanged, type);
    }

    function handleWaitingForSecondAnswerChange(value: WaitingForSecondAnswerEnum) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.waitingForSecondAnswer]: value});
        onFieldsChange();

        const type = {
            [WaitingForSecondAnswerEnum.yes]: 'yes',
            [WaitingForSecondAnswerEnum.no]: 'no',
            [WaitingForSecondAnswerEnum.noSelected]: 'no selected',
        }[value as WaitingForSecondAnswerEnum];

        trackAnalytics(ReviewsFilterFieldNameEnum.waitingForSecondAnswer, type);
    }

    function handleModeratedReviewChange(value: ModeratedEnum) {
        form.setFieldsValue({[ReviewsFilterFieldNameEnum.reviewModerated]: value});
        onFieldsChange();
    }

    function handleDataRangeChange(date: NoUndefinedRangeValueType<Dayjs> | null) {
        const [from, to] = date || [null, null];

        form.setFieldsValue({
            [ReviewsFilterFieldNameEnum.dateFrom]: from,
            [ReviewsFilterFieldNameEnum.dateTo]: to,
            [ReviewsFilterFieldNameEnum.collectionDateFrom]: null,
            [ReviewsFilterFieldNameEnum.collectionDateTo]: null,
        });
        onFieldsChange();

        trackAnalytics('Period', `${from} - ${to}`);
    }

    function handleCollectionDateRangeChange(date: NoUndefinedRangeValueType<Dayjs> | null) {
        const [from, to] = date || [null, null];

        form.setFieldsValue({
            [ReviewsFilterFieldNameEnum.collectionDateFrom]: from,
            [ReviewsFilterFieldNameEnum.collectionDateTo]: to,
            [ReviewsFilterFieldNameEnum.dateFrom]: null,
            [ReviewsFilterFieldNameEnum.dateTo]: null,
        });
        onFieldsChange();
    }

    function handleEmployeeReplyDateRangeChange(date: NoUndefinedRangeValueType<Dayjs> | null) {
        const [from, to] = date || [null, null];

        form.setFieldsValue({
            [ReviewsFilterFieldNameEnum.replyDateFrom]: from,
            [ReviewsFilterFieldNameEnum.replyDateTo]: to,
        });
        onFieldsChange();

        trackAnalytics('Employee reply date', `${from} - ${to}`);
    }

    const hasDoctors = reviewsDoctorList?.results && reviewsDoctorList.results.length > 0;

    const watchedDateFrom = Form.useWatch(ReviewsFilterFieldNameEnum.dateFrom, form);
    const watchedDateTo = Form.useWatch(ReviewsFilterFieldNameEnum.dateTo, form);
    const watchedCollectionDateFrom = Form.useWatch(ReviewsFilterFieldNameEnum.collectionDateFrom, form);
    const watchedCollectionDateTo = Form.useWatch(ReviewsFilterFieldNameEnum.collectionDateTo, form);
    const watchedReplyDateFrom = Form.useWatch(ReviewsFilterFieldNameEnum.replyDateFrom, form);
    const watchedReplyDateTo = Form.useWatch(ReviewsFilterFieldNameEnum.replyDateTo, form);

    const periodPickerValue = useMemo((): NoUndefinedRangeValueType<Dayjs> => {
        const publication: NoUndefinedRangeValueType<Dayjs> = [watchedDateFrom ?? dateFrom, watchedDateTo ?? dateTo];
        const collection: NoUndefinedRangeValueType<Dayjs> = [
            watchedCollectionDateFrom ?? collectionDateFrom,
            watchedCollectionDateTo ?? collectionDateTo,
        ];

        return periodPickerActiveTab === 'collection' ? collection : publication;
    }, [
        periodPickerActiveTab,
        watchedDateFrom,
        watchedDateTo,
        watchedCollectionDateFrom,
        watchedCollectionDateTo,
        dateFrom,
        dateTo,
        collectionDateFrom,
        collectionDateTo,
    ]);

    function handlePeriodPickerTabChange(activeKey: string) {
        const nextActiveTab: ReviewsSavedFilterPeriodPickerTabType =
            activeKey === 'collection' ? 'collection' : 'publication';

        setPeriodPickerActiveTab(nextActiveTab);
        saveCurrentFilterState(
            reviewsFilterStateToUrlObject(getReviewsFilterDataFromForm(form)),
            getCurrentSavedFilterUiState(nextActiveTab)
        );
    }

    useEffect(() => {
        setPeriodPickerActiveTab(savedFilterUiState.periodPickerActiveTab);
    }, [savedFilterUiState.periodPickerActiveTab]);

    useEffect(() => {
        setExpandedSections(getExpandedSectionsValue(isSavedFilterEnabled, savedFilterUiState.expandedSections));
    }, [isSavedFilterEnabled, savedFilterUiState.expandedSections]);

    useEffect(() => {
        const hasCollection =
            Boolean(reviewFilterState[ReviewsFilterFieldNameEnum.collectionDateFrom]) &&
            Boolean(reviewFilterState[ReviewsFilterFieldNameEnum.collectionDateTo]);
        const hasPublication =
            Boolean(reviewFilterState[ReviewsFilterFieldNameEnum.dateFrom]) &&
            Boolean(reviewFilterState[ReviewsFilterFieldNameEnum.dateTo]);

        if (hasCollection && periodPickerActiveTab !== 'collection') {
            setPeriodPickerActiveTab('collection');
        } else if (hasPublication && periodPickerActiveTab !== 'publication') {
            setPeriodPickerActiveTab('publication');
        }
    }, [
        periodPickerActiveTab,
        reviewFilterState[ReviewsFilterFieldNameEnum.dateFrom],
        reviewFilterState[ReviewsFilterFieldNameEnum.dateTo],
        reviewFilterState[ReviewsFilterFieldNameEnum.collectionDateFrom],
        reviewFilterState[ReviewsFilterFieldNameEnum.collectionDateTo],
    ]);

    useEffect(() => {
        form.setFieldsValue(reviewFilterState);
        form.setFieldsValue({
            [ReviewsFilterFieldNameEnum.dateFrom]: reviewFilterState['date-from'],
            [ReviewsFilterFieldNameEnum.dateTo]: reviewFilterState['date-to'],
            [ReviewsFilterFieldNameEnum.collectionDateFrom]: reviewFilterState['collection-date-from'],
            [ReviewsFilterFieldNameEnum.collectionDateTo]: reviewFilterState['collection-date-to'],
            [ReviewsFilterFieldNameEnum.replyDateFrom]: reviewFilterState['reply-date-from'],
            [ReviewsFilterFieldNameEnum.replyDateTo]: reviewFilterState['reply-date-to'],
        });
    }, [form, reviewFilterState]);

    const sourcesOptions = useMemo(
        () =>
            reviewsCatalogList
                ? reviewsCatalogList.map((catalog) => ({value: catalog.id, label: catalog.name || ''}))
                : [],
        [reviewsCatalogList]
    );

    useEffect(() => {
        const noPublicationDates = isNil(queries['date-from']) && isNil(queries['date-to']);
        const noCollectionDates = isNil(queries['collection-date-from']) && isNil(queries['collection-date-to']);
        const noReplyDates = isNil(queries['reply-date-from']) && isNil(queries['reply-date-to']);

        if (isBigBrand && noPublicationDates && noCollectionDates && noReplyDates) {
            const todayRaw = dayjs();
            const thirtyDaysAgoRaw = dayjs().subtract(30, 'day');

            form.setFieldsValue({
                [ReviewsFilterFieldNameEnum.dateFrom]: thirtyDaysAgoRaw,
                [ReviewsFilterFieldNameEnum.dateTo]: todayRaw,
                [ReviewsFilterFieldNameEnum.collectionDateFrom]: null,
                [ReviewsFilterFieldNameEnum.collectionDateTo]: null,
                [ReviewsFilterFieldNameEnum.replyDateFrom]: null,
                [ReviewsFilterFieldNameEnum.replyDateTo]: null,
            });
            onFieldsChange();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form, queries, reviewFilterState]);

    useEffect(() => {
        if (!reviewStatusList?.statuses) {
            return;
        }

        const validCodes = new Set(reviewStatusList.statuses.map((status) => status.statusCode));
        const currentDefault = reviewFilterState[ReviewsFilterFieldNameEnum.processStatus];
        const currentCustom = reviewFilterState[ReviewsFilterFieldNameEnum.customProcessStatus];
        const cleanedDefault = currentDefault.filter((code) => validCodes.has(code));
        const cleanedCustom = currentCustom.filter((code) => validCodes.has(code));

        if (cleanedDefault.length !== currentDefault.length || cleanedCustom.length !== currentCustom.length) {
            const updatedFilterState = {
                ...reviewFilterState,
                [ReviewsFilterFieldNameEnum.processStatus]: cleanedDefault,
                [ReviewsFilterFieldNameEnum.customProcessStatus]: cleanedCustom,
            };

            form.setFieldsValue({
                [ReviewsFilterFieldNameEnum.processStatus]: cleanedDefault,
                [ReviewsFilterFieldNameEnum.customProcessStatus]: cleanedCustom,
            });
            setReviewsStateUrlQuery(reviewsFilterStateToUrlObject(updatedFilterState));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reviewStatusList?.statuses]);

    const reviewsFilterSectionActiveMap = useMemo(
        (): Record<ReviewsFilterSectionEnum, boolean> => ({
            [ReviewsFilterSectionEnum.main]: isReviewsFilterMainSectionActive({
                reviewFilterState,
                dateFrom,
                dateTo,
                collectionDateFrom,
                collectionDateTo,
                hasDoctors,
            }),
            [ReviewsFilterSectionEnum.employee]: isReviewsFilterEmployeeSectionActive(
                reviewFilterState,
                replyDateFrom,
                replyDateTo
            ),
            [ReviewsFilterSectionEnum.tags]: isReviewsFilterTagsSectionActive(reviewFilterState),
            [ReviewsFilterSectionEnum.additional]: isReviewsFilterAdditionalSectionActive(reviewFilterState),
        }),
        [
            collectionDateFrom,
            collectionDateTo,
            dateFrom,
            dateTo,
            hasDoctors,
            replyDateFrom,
            replyDateTo,
            reviewFilterState,
        ]
    );

    const ratingOptions = useMemo(() => {
        const endpointRatings = Object.keys(reviewsStatisticResult?.ratingList || {}).map(Number);
        const mergedRatings = [...new Set([...RATING_FILTER_ORDER, ...endpointRatings])];

        return mergedRatings.map((rating) => ({
            rating,
            count: getRatingListCountForKey(reviewsStatisticResult?.ratingList, rating),
        }));
    }, [reviewsStatisticResult?.ratingList]);

    const totalReviewsLabelCount = useMemo(() => {
        if (!reviewsStatisticResult) {
            return 0;
        }

        const selectedRatings = reviewFilterState[ReviewsFilterFieldNameEnum.ratingList];

        if (selectedRatings.length === 0) {
            return reviewsStatisticResult.count;
        }

        return selectedRatings.reduce(
            (sum, rating) => sum + getRatingListCountForKey(reviewsStatisticResult.ratingList, rating),
            0
        );
    }, [reviewFilterState, reviewsStatisticResult]);

    const displayedAverageRating = useMemo(() => {
        if (!reviewsStatisticResult) {
            return 0;
        }

        const selectedRatings = reviewFilterState[ReviewsFilterFieldNameEnum.ratingList];

        return getWeightedAverageRatingForSelectedRatings(
            reviewsStatisticResult.ratingList,
            selectedRatings,
            reviewsStatisticResult.ratingAvg
        );
    }, [reviewFilterState, reviewsStatisticResult]);

    return {
        // form
        form,
        onFieldsChange,
        onFormReset,

        // state
        expandedSections,
        periodPickerActiveTab,
        reviewFilterState,
        reviewsFilterSectionActiveMap,
        isFilterEmpty,

        // period picker
        periodPickerValue,
        watchedReplyDateFrom,
        watchedReplyDateTo,
        replyDateFrom,
        replyDateTo,

        // rating
        ratingOptions,
        totalReviewsLabelCount,
        displayedAverageRating,
        isReviewsStatisticFetching,

        // sources
        sourcesOptions,
        reviewsCatalogListIsInProgress,

        // doctors
        hasDoctors,
        reviewsDoctorList,
        reviewsDoctorListIsInProgress,

        // statuses
        reviewStatusList,
        isReviewStatusListInProgress,

        // employees
        responsibleUsersList,
        isReviewResponsibleUsersInProgress,
        reviewsReplierList,
        reviewsReplierListIsInProgress,
        internalAgencyReviewLicense,

        // tags
        isShowAutotags,

        // saved filter
        isSavedFilterEnabled,
        isSavedFilterLoading,
        isSavedFilterSaving,

        // localization
        getLocalizedString,

        // handlers
        handleSectionsChange,
        handleSavedFilterToggle,
        handleSavedFilterKeyDown,
        handleDoctorsChange,
        handleSourceListChange,
        handleRatingChange,
        handleResponsibleEmployeeChange,
        handleRespondingEmployeeChange,
        handleStatusListChange,
        handleNewTagsChange,
        handleAgencyRepliersChange,
        handleWithoutTagChange,
        handleAutoReplyChange,
        handleContentOfReviewsChange,
        handleHasNotesChange,
        handleDeletedReviewsChange,
        handleRepliesToReviewsChange,
        handlePossibilityOfAnswerChange,
        handleReviewTypeChange,
        handleAbuseStatusChange,
        handleAutoAbuseChange,
        handleReviewChangedChange,
        handleWaitingForSecondAnswerChange,
        handleModeratedReviewChange,
        handleDataRangeChange,
        handleCollectionDateRangeChange,
        handleEmployeeReplyDateRangeChange,
        handlePeriodPickerTabChange,
    };
}
